import { Col, VerticalCenter } from "@/common/css";
import { MultiSelect } from "@/common/custom/MultiSelect";
import { Shimmer } from "@/common/shimmer/Shimmer";
import { ContentHead, LightP } from "@/common/ui/Text";
import RenderFormForModal from "@/components/Form/Renderer/RenderFormForModal";
import RenderLabel from "@/components/Form/Renderer/RenderLabel";
import { FormUIContext } from "@/context";
import { subscribeFirebase, unsubscribeFirebase } from "@/hooks/firebaseSubscribe";
import { useFetchConfig } from "@/services/common/config";
import { addUpdateDocument } from "@/services/common/profile.services";
import { setPageEvent } from "@/store/features/dashboardSlice";
import { loginSelector } from "@/store/features/loginSlice";
import { useAppDispatch } from "@/store/store";
import { DocumentFormSections } from "@/utils/constant/formConfig.constant";
import { toastMessage } from "@/utils/helper/myToast";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

interface UpdateDocumentModalProps {
  onClose: (props?: any) => void;
  documentInfo?: any;
}

const baseFormConfig = {
  submitBtnText: "Submit for review",
  showLabel: true,
  sections: [],
} as const;

const UpdateDocumentModal: React.FC<UpdateDocumentModalProps> = ({ onClose, documentInfo }) => {
  const { currentUser, currentOrg } = useSelector(loginSelector);
  const { data: options, isLoading }: any = useFetchConfig("DOCUMENT_OPTIONS", currentUser?.userType || "");
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const dispatch = useAppDispatch();

  const handleSubmit = useCallback(async (formData: any) => {
    const docs: any = Object.entries(formData)
      .map(([key, value]: any) => {
        let name, path;
        if (value && typeof value === "object" && "name" in value && "path" in value) {
          name = value.name;
          path = value.path;
        } else if (documentInfo?.doc && documentInfo.doc.documentKey === key) {
          name = documentInfo.doc.name;
          path = documentInfo.doc?.docPath;
        }
        if (name && path) {
          let documentNumber = undefined;
          const numberKey = `${key}-Number`;
          if (Object.prototype.hasOwnProperty.call(formData, numberKey)) {
            documentNumber = formData[numberKey];
          }
          return {
            documentKey: key,
            name,
            path,
            documentNumber,
            docId: documentInfo?.id,
          };
        }
        return null;
      })
      .filter(Boolean);
    const res: any = await addUpdateDocument(docs);
    // console.log({ res });
    if (res?.status === 200) {
      const { message, isError, showMessage } = res.data;
      onClose({ disabledWarning: true });
      const path = `/lystproof/${currentOrg?.SignupOrg?.ext_org_id}/documents`;
      subscribeFirebase(path, (data, error) => {
        if (error) {
          // handle error
          console.log("Error fetching data:", error);
        } else {
          // use data
          // console.log("Fetched data:", data);
          if (data?.length > 0) {
            dispatch(setPageEvent({ keys: ["documents"], type: "refresh" }));
            setTimeout(() => {
              dispatch(setPageEvent(null));
            }, 5000);
            unsubscribeFirebase(path);
          }
        }
      });
      if (showMessage) return toastMessage(message, isError);
    } else {
      console.log("Error", res);
      const { message, isError } = res?.response?.data || {};
      if (message) {
        return toastMessage(message, isError);
      }
    }
  }, []);

  const formConfig = useMemo(
    () => ({
      ...baseFormConfig,
      sections: selectedOptions.map((key: string) => DocumentFormSections[key as keyof typeof DocumentFormSections]),
    }),
    [selectedOptions]
  );

  const filteredOptions = useMemo(() => {
    if (!options) return [];
    return options.map((o: any) => ({
      label: o.name,
      value: o.key,
    }));
  }, [options]);

  useEffect(() => {
    if (documentInfo?.doc?.documentKey) {
      setSelectedOptions([documentInfo?.doc?.documentKey]);
    }
  }, [documentInfo]);

  return (
    <div className="flex flex-col justify-between h-full">
      <VerticalCenter className="h-full">
        <Shimmer isLoading={isLoading || !selectedOptions || selectedOptions?.length === 0} className="w-[calc(100%-32px)] h-10 mt-4 mx-4">
          <div className="px-4 w-full pt-4">
            <RenderLabel label="Select document" required className="pb-1.5" />
            <MultiSelect
              options={filteredOptions}
              onValueChange={setSelectedOptions}
              defaultValue={selectedOptions}
              placeholder="Select"
              variant="default"
              animation={2}
              maxCount={3}
              disabled
            />
          </div>
        </Shimmer>
        {/* Attachment Details */}
        {selectedOptions.length > 0 && (
          <Col className="w-full gap-1 px-4">
            <ContentHead text={`Attachment details(${selectedOptions.length})`} className=" mt-8 w-full" />
            <LightP text="Your file needs to be in JPEG, PNG, pdf format with a size of 10MB ." />
          </Col>
        )}
        {formConfig?.sections.length > 0 && (
          <FormUIContext.Provider value={{ isFormLoading, setIsFormLoading }}>
            <RenderFormForModal
              handleFormSubmit={handleSubmit}
              handleCancel={onClose}
              formConfig={formConfig}
              className="w-full h-full"
              btnContainerClass="!p-4 sticky bottom-0 bg-white"
              formClassName="h-[calc(100%-186px)]"
              defaultValues={{ ...documentInfo.doc, documentNumber: documentInfo?.doc_no }}
            />
          </FormUIContext.Provider>
        )}
      </VerticalCenter>
    </div>
  );
};

export default UpdateDocumentModal;
